import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getLivingProfile } from "@/lib/profile.functions";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Flame, Brain, Award, BookmarkCheck, Music, ShoppingBag,
  BookOpen, NotebookPen, Sparkles,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/me")({
  head: () => ({
    meta: [
      { title: "Your Living Profile · KAfrequency" },
      { name: "description", content: "Your consciousness level, streaks, badges, teachings, music, purchases, reading, and journal — all in one temple." },
    ],
  }),
  component: LivingProfile,
});

type Data = Awaited<ReturnType<typeof getLivingProfile>>;

function LivingProfile() {
  const load = useServerFn(getLivingProfile);
  const [d, setD] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    load().then(setD).catch((e) => setErr(e?.message ?? "Failed to load"));
  }, [load]);

  if (err) return <div className="p-6 text-sm text-red-400">{err}</div>;
  if (!d) return <div className="p-6 text-sm text-foreground/60">Loading your temple…</div>;

  const c = d.consciousness;
  const s = d.stats;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-6">
      {/* Identity + Consciousness Level */}
      <Card className="p-6">
        <div className="flex flex-col gap-6 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            {d.profile?.avatar_url ? (
              <img src={d.profile.avatar_url} alt="" className="h-20 w-20 rounded-full border border-gold/40 object-cover" />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-full border border-gold/40 bg-black/40 font-serif text-2xl text-gold-luminous">
                {(d.profile?.display_name ?? "K")[0]}
              </div>
            )}
            <div>
              <div className="font-serif text-2xl text-gold-luminous">{d.profile?.display_name ?? "Seeker"}</div>
              {d.profile?.bio && <p className="mt-1 max-w-md text-sm text-foreground/70">{d.profile.bio}</p>}
              <Link to="/account" className="mt-2 inline-block text-xs text-gold underline underline-offset-4">Edit profile →</Link>
            </div>
          </div>
          <div className="flex-1 rounded-md border hairline bg-black/30 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/60">
                <Sparkles size={14} className="text-gold" /> Consciousness Level
              </div>
              <div className="font-serif text-3xl text-gold-luminous">L{c.level}</div>
            </div>
            <div className="mt-1 text-sm text-foreground/80">{c.title}</div>
            <Progress value={Math.round(c.progress * 100)} className="mt-3 h-2" />
            <div className="mt-1 text-[11px] text-foreground/50">{Math.round(c.progress * 100)}% to L{c.level + 1} · {c.raw} pts</div>
          </div>
        </div>
      </Card>

      {/* Vital stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat icon={Flame} label="Streak" value={`${s.streakCurrent}d`} sub={`Best ${s.streakLongest}d`} />
        <Stat icon={Sparkles} label="Activations" value={s.activations} />
        <Stat icon={Brain} label="Meditation" value={`${s.meditationMinutes}m`} sub={`${s.meditationSessions} sessions`} />
        <Stat icon={Award} label="Badges" value={s.badges} />
        <Stat icon={BookmarkCheck} label="Saved teachings" value={d.favorites.teachings.length} />
        <Stat icon={Music} label="Favorite music" value={d.favorites.music.length} />
        <Stat icon={ShoppingBag} label="Purchases" value={s.orders} sub={`${s.activeEntitlements} active`} />
        <Stat icon={BookOpen} label="Reading" value={s.booksInProgress} sub="titles in progress" />
      </div>

      <Tabs defaultValue="badges" className="w-full">
        <TabsList className="flex w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="badges">Badges</TabsTrigger>
          <TabsTrigger value="teachings">Teachings</TabsTrigger>
          <TabsTrigger value="music">Music</TabsTrigger>
          <TabsTrigger value="purchases">Purchases</TabsTrigger>
          <TabsTrigger value="reading">Reading</TabsTrigger>
          <TabsTrigger value="meditation">Meditation</TabsTrigger>
          <TabsTrigger value="journal">Journal</TabsTrigger>
        </TabsList>

        <TabsContent value="badges" className="mt-4">
          {d.badges.length === 0 ? (
            <Empty text="No badges yet. Complete activations, keep streaks, and finish series to earn them." />
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {d.badges.map((b, i) => (
                <Card key={i} className="p-4 text-center">
                  <div className="text-2xl">{b.icon ?? "◈"}</div>
                  <div className="mt-2 font-serif text-sm text-gold-luminous">{b.title}</div>
                  {b.description && <div className="mt-1 text-xs text-foreground/60">{b.description}</div>}
                  <div className="mt-2 text-[10px] uppercase tracking-widest text-foreground/40">
                    {new Date(b.awarded_at).toLocaleDateString()}
                  </div>
                </Card>
              ))}
            </div>
          )}
          <div className="mt-3 text-right"><Link to="/journey/achievements" className="text-xs text-gold underline underline-offset-4">All achievements →</Link></div>
        </TabsContent>

        <TabsContent value="teachings" className="mt-4">
          <FavList items={d.favorites.teachings} emptyText="No saved teachings yet." linkBase="/wisdom" />
        </TabsContent>

        <TabsContent value="music" className="mt-4">
          <FavList items={d.favorites.music} emptyText="No favorite music yet." linkBase="/music" />
        </TabsContent>

        <TabsContent value="purchases" className="mt-4">
          {d.orders.length === 0 ? (
            <Empty text="No purchases yet." />
          ) : (
            <div className="space-y-2">
              {d.orders.map((o) => (
                <Card key={o.id} className="flex items-center justify-between p-3 text-sm">
                  <div>
                    <div className="font-medium text-foreground">{o.product_title ?? o.product_slug}</div>
                    <div className="text-xs text-foreground/60">
                      #{o.order_number ?? o.id.slice(0, 8)} · {new Date(o.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-foreground/80">${(o.amount_cents / 100).toFixed(2)} {o.currency?.toUpperCase()}</div>
                    <Badge variant="outline" className="mt-1 text-[10px]">{o.status}{o.fulfillment_status ? ` · ${o.fulfillment_status}` : ""}</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
          <div className="mt-3 text-right"><Link to="/downloads" className="text-xs text-gold underline underline-offset-4">Your downloads →</Link></div>
        </TabsContent>

        <TabsContent value="reading" className="mt-4">
          {d.reading.length === 0 ? (
            <Empty text="No books opened yet." />
          ) : (
            <div className="space-y-2">
              {d.reading.map((r) => (
                <Card key={r.title_id} className="p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="text-foreground/80">Title {r.title_id.slice(0, 8)}…</div>
                    <div className="text-xs text-foreground/60">{Math.round(Number(r.percent) || 0)}% · {Math.round((r.seconds_read ?? 0) / 60)}m</div>
                  </div>
                  <Progress value={Number(r.percent) || 0} className="mt-2 h-1.5" />
                </Card>
              ))}
            </div>
          )}
          <div className="mt-3 text-right"><Link to="/books" className="text-xs text-gold underline underline-offset-4">Continue reading →</Link></div>
        </TabsContent>

        <TabsContent value="meditation" className="mt-4">
          {d.recentPractice.length === 0 ? (
            <Empty text="No meditation logged yet." />
          ) : (
            <div className="space-y-2">
              {d.recentPractice.map((p, i) => (
                <Card key={i} className="flex items-center justify-between p-3 text-sm">
                  <div>
                    <div className="font-medium text-foreground capitalize">{p.kind}</div>
                    {p.note && <div className="text-xs text-foreground/60">{p.note}</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-foreground/80">{p.minutes}m</div>
                    <div className="text-[10px] text-foreground/40">{new Date(p.logged_on as string).toLocaleDateString()}</div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="journal" className="mt-4">
          {d.journal.length === 0 ? (
            <Empty text="No journal entries yet." />
          ) : (
            <div className="space-y-2">
              {d.journal.map((j) => (
                <Card key={j.id} className="p-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div className="font-serif text-gold-luminous">{j.title ?? "Untitled reflection"}</div>
                    <div className="text-[10px] text-foreground/40">{new Date(j.updated_at).toLocaleDateString()}</div>
                  </div>
                  <p className="mt-1 line-clamp-2 text-foreground/70">{j.body}</p>
                  {j.tags && j.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {j.tags.map((t: string) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
          <div className="mt-3 text-right"><Link to="/journey/journal" className="text-xs text-gold underline underline-offset-4">Open journal →</Link></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub }: { icon: React.ComponentType<{ size?: number; className?: string }>; label: string; value: React.ReactNode; sub?: string }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-foreground/60">
        <Icon size={12} className="text-gold" /> {label}
      </div>
      <div className="mt-1 font-serif text-2xl text-gold-luminous">{value}</div>
      {sub && <div className="text-[10px] text-foreground/40">{sub}</div>}
    </Card>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="rounded-md border hairline p-6 text-center text-sm text-foreground/60">{text}</div>;
}

function FavList({ items, emptyText, linkBase }: { items: { kind: string; slug: string; created_at: string }[]; emptyText: string; linkBase: string }) {
  if (items.length === 0) return <Empty text={emptyText} />;
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {items.map((f, i) => (
        <Card key={i} className="flex items-center justify-between p-3 text-sm">
          <div>
            <div className="font-medium text-foreground">{f.slug}</div>
            <div className="text-[10px] uppercase tracking-widest text-foreground/40">{f.kind}</div>
          </div>
          <a href={`${linkBase}/${f.slug}`} className="text-xs text-gold underline underline-offset-4">Open</a>
        </Card>
      ))}
    </div>
  );
}
